import React, { useState } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createAdNetwork, updateAdNetwork } from '../../api/adNetworkApi';
import { AdNetwork } from '../../types/ad-network';
import { cnpjMask, validaCNPJ } from '../../utils';

interface AddModalProps {
    adNetwork: AdNetwork|null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: AdNetwork) => void;
    onUpdateData: (newData: AdNetwork) => void;
}

const AddEditAdNetworkModal = ({ adNetwork, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const toggleAddModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: adNetwork?.id ? adNetwork.id : '',
            name: adNetwork?.name ? adNetwork.name : '',
            document: adNetwork?.document ? adNetwork.document : '',
            site: adNetwork?.site ? adNetwork.site : '',
            googleNetworkId: adNetwork?.googleNetworkId ? adNetwork.googleNetworkId: '',
            // users: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required('O nome é obrigatório'),
            document: Yup.string()
            .test('is-valid-cnpj', 'CNPJ inválido', value => {
                if (!value || value.trim() === '') return true;
                return validaCNPJ(value);
            }),
            site: Yup.string().trim().required('O site é obrigatório'),
            googleNetworkId: Yup.string().trim(),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            if (adNetwork) {
                updateAdNetwork(values)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Ad Network atualizada com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAdNetwork(values)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Ad Network cadastrada com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    {adNetwork ? 'Editar' : 'Adicionar'} ad network
                </ModalHeader>

                <ModalBody>
                    <div className="mb-3">
                        <Label
                            htmlFor="name"
                            className="form-label"
                        >
                            Nome
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                            invalid={
                                !!(handleForm.touched.name && handleForm.errors.name)
                            }
                        />
                        {handleForm.touched.name && handleForm.errors.name ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.name}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="document"
                            className="form-label"
                        >
                            Documento (CNPJ)
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="document"
                            name="document"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={cnpjMask(handleForm.values.document)}
                            invalid={
                                !!(handleForm.touched.document && handleForm.errors.document)
                            }
                        />
                        {handleForm.touched.document && handleForm.errors.document ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.document}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="site"
                            className="form-label"
                        >
                            Site
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="site"
                            name="site"
                            placeholder={"exemplo.com"}
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.site}
                            invalid={
                                !!(handleForm.touched.site && handleForm.errors.site)
                            }
                        />
                        {handleForm.touched.site && handleForm.errors.site ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.site}</FormFeedback>
                        ) : null}
                    </div>
                    <div className="mb-3">
                        <Label
                            htmlFor="googleNetworkId"
                            className="form-label"
                        >
                            Google Network ID
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="googleNetworkId"
                            name="googleNetworkId"
                            placeholder={"123456789"}
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.googleNetworkId}
                            invalid={
                                !!(handleForm.touched.googleNetworkId && handleForm.errors.googleNetworkId)
                            }
                        />
                        {handleForm.touched.googleNetworkId && handleForm.errors.googleNetworkId ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.googleNetworkId}</FormFeedback>
                        ) : null}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {adNetwork ? 'Editar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditAdNetworkModal;